import './add-edit-series-dialog.scss';

import React, { useContext, useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import isEqual from 'lodash/isEqual';
import { Context } from './shared/context';
import {
    IFieldListItem,
    IFormDataAxisDateSeries,
    IFormDataAxisNumericSeries,
    IFormDataAxisSeries,
} from './shared/interfaces';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import Card from '@components-clean/core/card';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import { DatePickerController } from '@components-clean/core/date-picker';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { TextBoxController } from '@components-clean/core/text-box';
import { NumericTextBoxController } from '@components-clean/core/text-box-numeric';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { FieldDefinitionFieldTypeDto } from '@interfaces-clean/field-rdl-definition.interface';
import { removeUndefinedAndEmpty } from '@utils-clean/form-value-comparer-helper';
import { TimelineAxisType, TimelineDateSeriesType, TimelineFieldLogicalOperator } from 'types/graphql-global-types';
import { useLocalState } from '@hooks/index';

interface IProps {
    formType: TimelineAxisType;
    series?: IFormDataAxisSeries;
    otherSeriesLabels: Set<string>;
    isEditing: boolean;
    onSubmit: (isNew: boolean, formData: IFormDataAxisSeries) => void;
    onClose: () => void;
}

const COMPONENT_NAME = 'add-edit-series-dialog';

const AddEditSeriesDialog = ({ formType, series, otherSeriesLabels, isEditing, onSubmit, onClose }: IProps) => {
    const { state } = useLocalState();

    const { t: tTimeline } = useTranslation('admin', { keyPrefix: 'managePanels.panelDetails.timeline' });
    const { t } = useTranslation('admin', { keyPrefix: 'managePanels.panelDetails.timeline.series.form' });

    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();

    const { listItems } = useContext(Context);

    const isNew = useMemo(() => !series, [series]);

    const dateSeries = series as IFormDataAxisDateSeries;
    const numericSeries = series as IFormDataAxisNumericSeries;

    const defaultFormValues = useMemo(
        () => ({
            id: series?.id,
            label: series?.label ?? '',
            rowData: series?.rowData ?? { source: null, rule: undefined },
            ...(formType === TimelineAxisType.DATE && {
                type: dateSeries?.type ?? listItems.dateSeriesTypes[0],
                startField: dateSeries?.startField ?? null,
                endField: dateSeries?.endField ?? null,
                plotLabel: dateSeries?.plotLabel ?? null,
            }),
            ...(formType === TimelineAxisType.NUMERIC && {
                type: numericSeries?.type ?? listItems.numericSeriesTypes[0],
                yPlotField: numericSeries?.yPlotField ?? null,
                dateField: numericSeries?.dateField ?? null,
                threshold: {
                    value: numericSeries?.threshold?.value,
                    field: numericSeries?.threshold?.field,
                },
            }),
        }),
        [series, formType],
    );

    const {
        control,
        setValue,
        handleSubmit,
        watch,
        formState: { isValid, isDirty },
    } = useForm<IFormDataAxisDateSeries | IFormDataAxisNumericSeries>({
        defaultValues: defaultFormValues,
        mode: 'all',
    });

    const type = useWatch({ control, name: 'type' });
    const source = useWatch({ control, name: 'rowData.source' });
    const rule = useWatch({ control, name: 'rowData.rule' });
    const threshold = useWatch({ control, name: 'threshold' });

    const [isThresholdUsingFields, setIsThresholdUsingFields] = React.useState<boolean>(
        !!(series as IFormDataAxisNumericSeries)?.threshold?.field,
    );

    const rawFields = useMemo(() => {
        const selectedRowDataType = state!.cm2DataSources!.find((ds) => !!ds && ds.id === source?.id)?.table;

        return state!.cm2Fields!.filter((f) => !!f).filter((f) => !f.isHidden && f.dataType === selectedRowDataType);
    }, [source]);

    const getFieldSpecificOperators = (field?: IFieldListItem | null) => {
        const fieldType = field?.fieldType;

        if (!fieldType) {
            return [];
        }

        const includedOperators = [TimelineFieldLogicalOperator.EQUAL, TimelineFieldLogicalOperator.NOT_EQUAL];

        switch (fieldType) {
            case FieldDefinitionFieldTypeDto.TEXT:
                includedOperators.push(
                    TimelineFieldLogicalOperator.STARTS_WITH,
                    TimelineFieldLogicalOperator.ENDS_WITH,
                    TimelineFieldLogicalOperator.CONTAINS,
                    TimelineFieldLogicalOperator.DOES_NOT_CONTAIN,
                );
                break;
            case FieldDefinitionFieldTypeDto.DATE:
            case FieldDefinitionFieldTypeDto.PARTIAL_DATE:
            case FieldDefinitionFieldTypeDto.INT:
            case FieldDefinitionFieldTypeDto.FLOAT:
                includedOperators.push(
                    TimelineFieldLogicalOperator.GREATER_THAN,
                    TimelineFieldLogicalOperator.GREATER_THAN_OR_EQUAL,
                    TimelineFieldLogicalOperator.LESS_THAN,
                    TimelineFieldLogicalOperator.LESS_THAN_OR_EQUAL,
                );
                break;
            case FieldDefinitionFieldTypeDto.BOOL:
                break;
            default:
                break;
        }

        includedOperators.push(TimelineFieldLogicalOperator.IS_BLANK, TimelineFieldLogicalOperator.IS_NOT_BLANK);

        return listItems.operators.filter((operator) =>
            includedOperators.includes(operator.id as TimelineFieldLogicalOperator),
        );
    };

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, [isDirty, setComponentHasChanges]);

    const currentValues = watch();
    const normalizedDefault = removeUndefinedAndEmpty(defaultFormValues);
    const normalizedCurrent = removeUndefinedAndEmpty(currentValues);
    const isChanged = !isEqual(normalizedDefault, normalizedCurrent);

    return (
        <DraggableDialog
            title={isNew ? t('titles.add') : t('titles.edit')}
            dataTestId="add-edit-dialog"
            className="add-edit-timeline-series-dialog"
            onSubmit={async () => {
                await handleSubmit((data) => {
                    const formData = {
                        ...data,
                        rowData: {
                            ...data.rowData,
                            rule: data.rowData.rule?.field1
                                ? {
                                      field1: data.rowData.rule.field1,
                                      operator1: data.rowData.rule.operator1,
                                      value1: data.rowData.rule.value1,
                                      logicalOperator: data.rowData.rule.logicalOperator,
                                      field2: data.rowData.rule.field2,
                                      operator2: data.rowData.rule.operator2,
                                      value2: data.rowData.rule.value2,
                                  }
                                : undefined,
                        },
                        threshold: (data as IFormDataAxisNumericSeries).threshold && {
                            value: !isThresholdUsingFields
                                ? {
                                      upper: (data as IFormDataAxisNumericSeries).threshold?.value?.upper ?? null,
                                      lower: (data as IFormDataAxisNumericSeries).threshold?.value?.lower ?? null,
                                  }
                                : undefined,
                            field: isThresholdUsingFields
                                ? {
                                      upper: (data as IFormDataAxisNumericSeries).threshold?.field?.upper ?? null,
                                      lower: (data as IFormDataAxisNumericSeries).threshold?.field?.lower ?? null,
                                  }
                                : undefined,
                        },
                    };
                    onSubmit(isNew, formData);
                })();
            }}
            onClose={async () => {
                if (componentHasChanges(COMPONENT_NAME)) {
                    const confirmed = await confirmDiscardChanges();
                    if (!confirmed) return;
                }
                onClose();
            }}
            primaryBtnLabel={isNew ? t('buttons.add') : t('buttons.update')}
            secondaryBtnLabel={t('buttons.cancel')}
            initialWidth={640}
            initialHeight={500}
            primaryBtnDisabled={!isValid || !isDirty || !isChanged}
        >
            <TextBoxController
                label={t('labels.label')}
                data-testid="label"
                name="label"
                control={control}
                readOnly={!isEditing}
                required
                maxLength={30}
                validateRules={{
                    labelMustBeUnique: (label: string | null) => {
                        if (!label || otherSeriesLabels.size === 0) {
                            return true;
                        }
                        if (otherSeriesLabels.has(label.trim().toLowerCase())) {
                            return tTimeline('validationErrors.uniqueSeriesLabels');
                        }
                        return true;
                    },
                }}
            />
            <BasicDropDownController
                label={t('labels.type')}
                data-testid="type"
                name="type"
                control={control}
                dataItemKey="id"
                textField="text"
                readOnly={!isEditing}
                data={formType === TimelineAxisType.DATE ? listItems.dateSeriesTypes : listItems.numericSeriesTypes}
            />

            <BasicDropDownController
                label={t('labels.rowData')}
                data-testid="row-data"
                name="rowData.source"
                control={control}
                filterable
                dataItemKey="id"
                textField="text"
                allowClear
                required
                readOnly={!isEditing}
                data={listItems.rowData}
                onChange={() => {
                    // Clear row data
                    setValue('rowData.rule.field1', null);
                    setValue('rowData.rule.operator1', null);
                    setValue('rowData.rule.value1', '');
                    setValue('rowData.rule.logicalOperator', null);
                    setValue('rowData.rule.field2', null);
                    setValue('rowData.rule.operator2', null);
                    setValue('rowData.rule.value2', '');

                    // Clear any subsequent date fields
                    setValue('startField', null);
                    setValue('endField', null);
                    setValue('plotLabel', null);

                    // Clear any numeric fields
                    setValue('yPlotField', null);
                    setValue('dateField', null);
                    setValue('threshold', {
                        value: { lower: null, upper: null },
                        field: { lower: null, upper: null },
                    });
                    setIsThresholdUsingFields(false);
                }}
            />

            <br />

            {source && (
                <Card className="rules" title={t('labels.filterRule.title')}>
                    <div className="rule-part">
                        <BasicDropDownController
                            label={t('labels.filterRule.field1')}
                            data-testid="field1"
                            name="rowData.rule.field1"
                            control={control}
                            filterable
                            dataItemKey="id"
                            textField="text"
                            allowClear
                            readOnly={!isEditing}
                            data={listItems.fields.filter((fieldListItem) =>
                                rawFields.map((f) => f.key).includes(fieldListItem.id),
                            )}
                            required={!!rule?.operator1 || !!rule?.value1}
                            onChange={() => {
                                setValue('rowData.rule.operator1', null);
                                setValue('rowData.rule.value1', null);
                            }}
                        />
                        {rule?.field1 && (
                            <BasicDropDownController
                                label={t('labels.filterRule.operator1')}
                                data-testid="operator1"
                                name="rowData.rule.operator1"
                                control={control}
                                filterable
                                dataItemKey="id"
                                textField="text"
                                allowClear
                                readOnly={!isEditing}
                                data={getFieldSpecificOperators(rule?.field1)}
                                required={!!rule?.field1 || !!rule?.value1}
                            />
                        )}
                        {[
                            TimelineFieldLogicalOperator.CONTAINS,
                            TimelineFieldLogicalOperator.DOES_NOT_CONTAIN,
                            TimelineFieldLogicalOperator.ENDS_WITH,
                            TimelineFieldLogicalOperator.EQUAL,
                            TimelineFieldLogicalOperator.GREATER_THAN,
                            TimelineFieldLogicalOperator.GREATER_THAN_OR_EQUAL,
                            TimelineFieldLogicalOperator.LESS_THAN,
                            TimelineFieldLogicalOperator.LESS_THAN_OR_EQUAL,
                            TimelineFieldLogicalOperator.NOT_EQUAL,
                            TimelineFieldLogicalOperator.STARTS_WITH,
                        ].includes(rule?.operator1?.id as TimelineFieldLogicalOperator) && (
                            <>
                                {rule?.field1?.fieldType === FieldDefinitionFieldTypeDto.TEXT && (
                                    <TextBoxController
                                        label={t('labels.filterRule.value1')}
                                        data-testid="value1"
                                        name="rowData.rule.value1"
                                        control={control}
                                        readOnly={!isEditing}
                                    />
                                )}
                                {rule?.field1?.fieldType === FieldDefinitionFieldTypeDto.BOOL && (
                                    <BasicDropDownController
                                        label={t('labels.filterRule.value1')}
                                        data-testid="value1"
                                        name="rowData.rule.value1"
                                        control={control}
                                        filterable
                                        dataItemKey="id"
                                        textField="text"
                                        allowClear
                                        readOnly={!isEditing}
                                        data={listItems.booleanOperators}
                                    />
                                )}
                                {(rule?.field1?.fieldType === FieldDefinitionFieldTypeDto.INT ||
                                    rule?.field1?.fieldType === FieldDefinitionFieldTypeDto.FLOAT) && (
                                    <NumericTextBoxController
                                        label={t('labels.filterRule.value1')}
                                        data-testid="value1"
                                        name="rowData.rule.value1"
                                        control={control}
                                        readOnly={!isEditing}
                                        format={
                                            rule.field1.fieldType === FieldDefinitionFieldTypeDto.INT ? '#' : '0.##'
                                        }
                                    />
                                )}
                                {(rule?.field1?.fieldType === FieldDefinitionFieldTypeDto.DATE ||
                                    rule?.field1?.fieldType === FieldDefinitionFieldTypeDto.PARTIAL_DATE) && (
                                    <DatePickerController
                                        label={t('labels.filterRule.value1')}
                                        data-testid="value1"
                                        name="rowData.rule.value1"
                                        control={control as never}
                                        readonly={!isEditing}
                                        fillMode="solid"
                                    />
                                )}
                            </>
                        )}
                    </div>

                    <div className="rule-logical-operator">
                        <BasicDropDownController
                            label={t('labels.filterRule.logicalOperator')}
                            data-testid="logical-operator"
                            name="rowData.rule.logicalOperator"
                            control={control}
                            filterable
                            dataItemKey="id"
                            textField="text"
                            allowClear
                            readOnly={!isEditing}
                            data={listItems.ruleOperators}
                        />
                    </div>

                    {rule?.logicalOperator && (
                        <div className="rule-part">
                            <BasicDropDownController
                                label={t('labels.filterRule.field2')}
                                data-testid="field2"
                                name="rowData.rule.field2"
                                control={control}
                                filterable
                                dataItemKey="id"
                                textField="text"
                                allowClear
                                readOnly={!isEditing}
                                data={listItems.fields.filter((fieldListItem) =>
                                    rawFields.map((f) => f.key).includes(fieldListItem.id),
                                )}
                                required={!!rule?.logicalOperator}
                                onChange={() => {
                                    setValue('rowData.rule.operator2', null);
                                    setValue('rowData.rule.value2', null);
                                }}
                            />
                            {rule?.field2 && (
                                <BasicDropDownController
                                    label={t('labels.filterRule.operator2')}
                                    data-testid="operator2"
                                    name="rowData.rule.operator2"
                                    control={control}
                                    filterable
                                    dataItemKey="id"
                                    textField="text"
                                    allowClear
                                    readOnly={!isEditing}
                                    data={getFieldSpecificOperators(rule?.field2)}
                                    required={!!rule?.logicalOperator}
                                />
                            )}
                            {[
                                TimelineFieldLogicalOperator.CONTAINS,
                                TimelineFieldLogicalOperator.DOES_NOT_CONTAIN,
                                TimelineFieldLogicalOperator.ENDS_WITH,
                                TimelineFieldLogicalOperator.EQUAL,
                                TimelineFieldLogicalOperator.GREATER_THAN,
                                TimelineFieldLogicalOperator.GREATER_THAN_OR_EQUAL,
                                TimelineFieldLogicalOperator.LESS_THAN,
                                TimelineFieldLogicalOperator.LESS_THAN_OR_EQUAL,
                                TimelineFieldLogicalOperator.NOT_EQUAL,
                                TimelineFieldLogicalOperator.STARTS_WITH,
                            ].includes(rule?.operator2?.id as TimelineFieldLogicalOperator) && (
                                <>
                                    {rule?.field2?.fieldType === FieldDefinitionFieldTypeDto.TEXT && (
                                        <TextBoxController
                                            label={t('labels.filterRule.value2')}
                                            data-testid="value2"
                                            name="rowData.rule.value2"
                                            control={control}
                                            readOnly={!isEditing}
                                        />
                                    )}
                                    {rule?.field2?.fieldType === FieldDefinitionFieldTypeDto.BOOL && (
                                        <BasicDropDownController
                                            label={t('labels.filterRule.value2')}
                                            data-testid="value2"
                                            name="rowData.rule.value2"
                                            control={control}
                                            filterable
                                            dataItemKey="id"
                                            textField="text"
                                            allowClear
                                            readOnly={!isEditing}
                                            data={listItems.booleanOperators}
                                        />
                                    )}
                                    {(rule?.field2?.fieldType === FieldDefinitionFieldTypeDto.INT ||
                                        rule?.field2?.fieldType === FieldDefinitionFieldTypeDto.FLOAT) && (
                                        <NumericTextBoxController
                                            label={t('labels.filterRule.value2')}
                                            data-testid="value2"
                                            name="rowData.rule.value2"
                                            control={control}
                                            readOnly={!isEditing}
                                            format={
                                                rule.field2.fieldType === FieldDefinitionFieldTypeDto.INT ? '#' : '0.##'
                                            }
                                        />
                                    )}
                                    {(rule?.field2?.fieldType === FieldDefinitionFieldTypeDto.DATE ||
                                        rule?.field2?.fieldType === FieldDefinitionFieldTypeDto.PARTIAL_DATE) && (
                                        <DatePickerController
                                            label={t('labels.filterRule.value2')}
                                            data-testid="value2"
                                            name="rowData.rule.value2"
                                            control={control as never}
                                            readonly={!isEditing}
                                            fillMode="solid"
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </Card>
            )}

            {formType === TimelineAxisType.DATE && (
                <>
                    <BasicDropDownController
                        label={t('labels.dateSeries.startField')}
                        data-testid="start-date"
                        name="startField"
                        control={control}
                        filterable
                        dataItemKey="id"
                        textField="text"
                        required
                        readOnly={!isEditing}
                        data={listItems.fields.filter((fieldListItem) =>
                            rawFields
                                .filter(
                                    (f) =>
                                        f.fieldType === FieldDefinitionFieldTypeDto.DATE ||
                                        f.fieldType === FieldDefinitionFieldTypeDto.PARTIAL_DATE,
                                )
                                .map((f) => f.key)
                                .includes(fieldListItem.id),
                        )}
                    />
                    {type.id === TimelineDateSeriesType.DATE_RANGE ? (
                        <BasicDropDownController
                            label={t('labels.dateSeries.endField')}
                            data-testid="end-date"
                            name="endField"
                            control={control}
                            filterable
                            dataItemKey="id"
                            textField="text"
                            required
                            readOnly={!isEditing}
                            data={listItems.fields.filter((fieldListItem) =>
                                rawFields
                                    .filter(
                                        (f) =>
                                            f.fieldType === FieldDefinitionFieldTypeDto.DATE ||
                                            f.fieldType === FieldDefinitionFieldTypeDto.PARTIAL_DATE,
                                    )
                                    .map((f) => f.key)
                                    .includes(fieldListItem.id),
                            )}
                        />
                    ) : (
                        <div />
                    )}
                    <BasicDropDownController
                        label={t('labels.dateSeries.plotLabel')}
                        data-testid="plot-label"
                        name="plotLabel"
                        control={control}
                        filterable
                        dataItemKey="id"
                        textField="text"
                        required
                        readOnly={!isEditing}
                        data={listItems.fields.filter((fieldListItem) =>
                            rawFields
                                .filter((f) => f.fieldType === FieldDefinitionFieldTypeDto.TEXT)
                                .map((f) => f.key)
                                .includes(fieldListItem.id),
                        )}
                    />
                </>
            )}
            {formType === TimelineAxisType.NUMERIC && (
                <>
                    <BasicDropDownController
                        label={t('labels.numericSeries.yPlotField')}
                        data-testid="y-plot-field"
                        name="yPlotField"
                        control={control}
                        filterable
                        dataItemKey="id"
                        textField="text"
                        required
                        readOnly={!isEditing}
                        data={listItems.fields.filter((fieldListItem) =>
                            rawFields
                                .filter(
                                    (f) =>
                                        f.fieldType === FieldDefinitionFieldTypeDto.INT ||
                                        f.fieldType === FieldDefinitionFieldTypeDto.FLOAT,
                                )
                                .map((f) => f.key)
                                .includes(fieldListItem.id),
                        )}
                    />
                    <BasicDropDownController
                        label={t('labels.numericSeries.dateField')}
                        data-testid="date-field"
                        name="dateField"
                        control={control}
                        filterable
                        dataItemKey="id"
                        textField="text"
                        required
                        allowClear
                        readOnly={!isEditing}
                        data={listItems.fields.filter((fieldListItem) =>
                            rawFields
                                .filter(
                                    (f) =>
                                        f.fieldType === FieldDefinitionFieldTypeDto.DATE ||
                                        f.fieldType === FieldDefinitionFieldTypeDto.PARTIAL_DATE,
                                )
                                .map((f) => f.key)
                                .includes(fieldListItem.id),
                        )}
                    />
                </>
            )}

            {formType === TimelineAxisType.NUMERIC && (
                <>
                    <hr />

                    <Card className="threshold" title="Threshold">
                        <CoreCheckbox
                            label={t('labels.numericSeries.threshold.useFields')}
                            data-testid="use-fields"
                            disabled={!isEditing}
                            checked={isThresholdUsingFields}
                            onChange={(e) => {
                                setIsThresholdUsingFields(e.target.value as boolean);
                            }}
                        />
                        <br />

                        {isThresholdUsingFields ? (
                            <>
                                <BasicDropDownController
                                    label={t('labels.numericSeries.threshold.lower')}
                                    data-testid="lower-threshold"
                                    name="threshold.field.lower"
                                    control={control}
                                    filterable
                                    dataItemKey="id"
                                    textField="text"
                                    allowClear
                                    readOnly={!isEditing}
                                    data={listItems.fields.filter((fieldListItem) =>
                                        rawFields
                                            .filter(
                                                (f) =>
                                                    f.fieldType === FieldDefinitionFieldTypeDto.INT ||
                                                    f.fieldType === FieldDefinitionFieldTypeDto.FLOAT,
                                            )
                                            .map((f) => f.key)
                                            .includes(fieldListItem.id),
                                    )}
                                />
                                <BasicDropDownController
                                    label={t('labels.numericSeries.threshold.upper')}
                                    data-testid="upper-threshold"
                                    name="threshold.field.upper"
                                    control={control}
                                    filterable
                                    dataItemKey="id"
                                    textField="text"
                                    readOnly={!isEditing}
                                    data={listItems.fields.filter((fieldListItem) =>
                                        rawFields
                                            .filter(
                                                (f) =>
                                                    f.fieldType === FieldDefinitionFieldTypeDto.INT ||
                                                    f.fieldType === FieldDefinitionFieldTypeDto.FLOAT,
                                            )
                                            .map((f) => f.key)
                                            .includes(fieldListItem.id),
                                    )}
                                />
                            </>
                        ) : (
                            <>
                                <NumericTextBoxController
                                    label={t('labels.numericSeries.threshold.lower')}
                                    data-testid="lower-threshold"
                                    name="threshold.value.lower"
                                    control={control}
                                    readOnly={!isEditing}
                                    validateRules={{
                                        _: (value: number | null) => {
                                            if (!value || threshold.value?.upper === null) {
                                                return true;
                                            }
                                            if (value > threshold.value!.upper) {
                                                return 'Cannot be greater than the upper threshold value.';
                                            }
                                        },
                                    }}
                                />
                                <NumericTextBoxController
                                    label={t('labels.numericSeries.threshold.upper')}
                                    data-testid="upper-threshold"
                                    name="threshold.value.upper"
                                    control={control}
                                    readOnly={!isEditing}
                                    validateRules={{
                                        _: (value: number | null) => {
                                            if (!value || threshold.value?.lower === null) {
                                                return true;
                                            }
                                            if (value < threshold.value!.lower) {
                                                return 'Cannot be less than the lower threshold value.';
                                            }
                                        },
                                    }}
                                />
                            </>
                        )}
                    </Card>
                </>
            )}
        </DraggableDialog>
    );
};

export default AddEditSeriesDialog;
